// The hifz methods and the learner's remembered plan. No imports on purpose:
// the journey (progress.ts) normalises a stored plan, and must not pull in the
// Supabase client to do it.

// ── Methods ─────────────────────────────────────────────────────────────────

export type HifzMethod = "classic" | "para" | "juzAmma" | "surahs" | "revision";

/** The learner's standing choice, kept in their journey. */
export type HifzPlan = {
  method: HifzMethod;
  /** New ayahs a day (the sabaq). */
  dailyAyahs: number;
  /** How many days the manzil takes to come round (method-dependent). */
  cycleDays: number;
  /** Listening loop in the reader: each ayah ×, whole portion ×. */
  each: number;
  rounds: number;
  /** Surahs for the "chosen surahs" method, in order. */
  surahs: number[];
  /** Day 1 of the cycle, "YYYY-MM-DD". */
  startedOn: string;
  /** Open revision (sabqi, manzil) with the text hidden, to test recall. */
  recall: boolean;
  updatedAt: string;
};

export const METHODS: Record<HifzMethod, { en: string; ur: string; about: string; aboutUr: string; cycles: number[] }> = {
  classic: {
    en: "Sabaq, sabqi, manzil (madrasa method)",
    ur: "سبق، سبقی، منزل (مدرسہ طریقہ)",
    about: "A few new ayahs every day, continuing from the last lesson. Sabqi revises the past week's lessons; manzil revises everything older, a slice a day.",
    aboutUr: "ہر روز چند نئی آیات، پچھلے سبق سے آگے۔ سبقی پچھلے ہفتے کے اسباق دہراتی ہے؛ منزل اس سے پرانا سب کچھ، روزانہ ایک حصہ۔",
    cycles: [7, 14, 30],
  },
  para: {
    en: "Para by para — 30-day cycle",
    ur: "پارہ بہ پارہ — ۳۰ دن کا چکر",
    about: "Each day belongs to one para: day 1 is para 1, day 2 is para 2, and so on for 30 days, then the cycle starts again. On its day you learn a few new ayahs of that para and revise what you already hold of it; yesterday's para is today's manzil.",
    aboutUr: "ہر دن ایک پارے کا ہے: پہلا دن پہلا پارہ، دوسرا دن دوسرا پارہ… ۳۰ دن تک، پھر چکر دوبارہ۔ اس دن اسی پارے کی چند نئی آیات یاد کریں اور جو یاد ہے وہ دہرائیں؛ کل کا پارہ آج کی منزل ہے۔",
    cycles: [30],
  },
  juzAmma: {
    en: "Juz Amma first (from An-Nas upwards)",
    ur: "پہلے عم پارہ (الناس سے اوپر)",
    about: "Start with the short surahs at the end — An-Nas, Al-Falaq, Al-Ikhlas — then move upwards one surah at a time. How most children begin.",
    aboutUr: "آخری چھوٹی سورتوں سے آغاز — الناس، الفلق، الاخلاص — پھر ایک ایک سورت اوپر۔ اکثر بچے یہیں سے شروع کرتے ہیں۔",
    cycles: [7, 14, 30],
  },
  surahs: {
    en: "Chosen surahs",
    ur: "منتخب سورتیں",
    about: "Memorise the surahs you choose, in order — Ya-Sin, Ar-Rahman, Al-Waqi'ah, Al-Mulk and Al-Kahf to start with.",
    aboutUr: "اپنی منتخب سورتیں ترتیب سے یاد کریں — شروع میں یٰسین، الرحمٰن، الواقعہ، الملک اور الکہف۔",
    cycles: [7, 14, 30],
  },
  revision: {
    en: "Revision only (for a hafiz)",
    ur: "صرف دہرائی (حافظ کے لیے)",
    about: "No new lesson — keep what you hold. One para a day finishes the Quran every 30 days (two a day: every 15; three: every 10). If nothing is recorded yet, the whole Quran is treated as memorised.",
    aboutUr: "نیا سبق نہیں — جو یاد ہے اسے پکا رکھیں۔ روزانہ ایک پارہ یعنی ہر ۳۰ دن میں پورا قرآن (دو پارے: ۱۵ دن، تین: ۱۰ دن)۔ اگر ابھی کچھ درج نہیں تو پورا قرآن یاد سمجھا جائے گا۔",
    cycles: [30, 15, 10],
  },
};

export const REPETITION_PRESETS = [
  { each: 3, rounds: 1, en: "Gentle — each ayah 3×", ur: "نرم — ہر آیت ۳ بار" },
  { each: 5, rounds: 2, en: "Steady — 5× each, portion 2×", ur: "مستحکم — ہر آیت ۵ بار، حصہ ۲ بار" },
  { each: 10, rounds: 3, en: "Intensive — 10× each, portion 3×", ur: "سخت — ہر آیت ۱۰ بار، حصہ ۳ بار" },
];

export function defaultPlan(today: string): HifzPlan {
  return {
    method: "classic", dailyAyahs: 5, cycleDays: 7, each: 5, rounds: 2,
    surahs: [36, 55, 56, 67, 18], startedOn: today, recall: true, updatedAt: new Date().toISOString(),
  };
}

const DAY = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const clampInt = (value: unknown, min: number, max: number, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) ? Math.min(max, Math.max(min, Math.round(value))) : fallback;

/** A stored plan made safe, or undefined if there is none. */
export function normalizePlan(value: unknown, today: string): HifzPlan | undefined {
  if (!value || typeof value !== "object") return undefined;
  const input = value as Partial<HifzPlan>;
  if (!input.method || !(input.method in METHODS)) return undefined;
  const base = defaultPlan(today);
  const cycles = METHODS[input.method].cycles;
  const cycle = clampInt(input.cycleDays, 1, 60, cycles[0]);
  return {
    method: input.method,
    dailyAyahs: clampInt(input.dailyAyahs, 1, 50, base.dailyAyahs),
    cycleDays: cycles.includes(cycle) ? cycle : cycles[0],
    each: clampInt(input.each, 1, 20, base.each),
    rounds: clampInt(input.rounds, 1, 10, base.rounds),
    surahs: Array.isArray(input.surahs)
      ? [...new Set(input.surahs.filter((surah): surah is number => Number.isInteger(surah) && surah >= 1 && surah <= 114))].slice(0, 114)
      : base.surahs,
    startedOn: typeof input.startedOn === "string" && DAY.test(input.startedOn) ? input.startedOn : today,
    recall: typeof input.recall === "boolean" ? input.recall : base.recall,
    updatedAt: typeof input.updatedAt === "string" ? input.updatedAt.slice(0, 40) : base.updatedAt,
  };
}
